import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import cacheKeys from "@/config/cache-keys";
import useFormulas from "@/hooks/useFormulas";
import AppService from "@/services/AppService";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

const appService = new AppService();

export default function InsurancePayoutTable({
	currency,
}: {
	currency: "NOK" | "G";
}) {
	const { t } = useTranslation();

	const formulas = useFormulas();

	const { id } = useParams();

	const query = useQuery({
		queryKey: cacheKeys.CLIENT_DATA,
		queryFn: async () => appService.getClientData(id as string),
		enabled: !!id,
	});

	if (query.isLoading) {
		return <div>Loading...</div>;
	}

	if (query.isError) {
		return <div>Error: {query.error.message}</div>;
	}

	if (!query.data) {
		return <div>No data available</div>;
	}

	// Calculate the same values as the chart
	const gValue = query.data.Result.gvalue;

	const formatValue = (value: number) => {
		// Defensive: avoid rendering "NaN G" or "Infinity G" in the UI
		if (!Number.isFinite(value)) {
			return `0 ${currency}`;
		}
		return `${value.toLocaleString()} ${currency}`;
	};

	const getTotalPayout = (
		payoutType: "invalidity" | "disability" | "death",
		at: "work" | "other",
		causedBy: "accident" | "illness"
	) => {
		const getPayoutFn =
			payoutType === "invalidity"
				? formulas.getInvalidityPayout
				: payoutType === "disability"
				? formulas.getDisabilityPayout
				: formulas.getDeathPayout;

		const employer = getPayoutFn(
			{ at, causedBy, show: "employer", currency },
			gValue
		);
		const compulsory = getPayoutFn(
			{ at, causedBy, show: "compulsory", currency },
			gValue
		);

		const total = employer + compulsory;

		if (!Number.isFinite(total)) {
			// Log enough context to debug which insurance/params caused NaN
			// eslint-disable-next-line no-console
			console.error("NaN total payout in InsurancePayoutTable", {
				payoutType,
				at,
				causedBy,
				employer,
				compulsory,
				age: formulas.age,
				annualSalary: formulas.annualSalary,
				maritalStatus: formulas.maritalStatus,
				childAges: formulas.childAges,
				insurances: formulas.insurances,
			});
			return formatValue(0);
		}

		return formatValue(total);
	};

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead></TableHead>
					<TableHead colSpan={2} className="text-center text-soft-white">
						{t("calculator.chart.benefits.accident")}
					</TableHead>
					<TableHead colSpan={2} className="text-center text-soft-white">
						{t("calculator.chart.benefits.illness")}
					</TableHead>
				</TableRow>
				<TableRow>
					<TableHead></TableHead>
					<TableHead className="text-center text-soft-white">
						{t("calculator.chart.benefits.work")}
					</TableHead>
					<TableHead className="text-center text-soft-white">
						{t("calculator.chart.benefits.other")}
					</TableHead>
					<TableHead className="text-center text-soft-white">
						{t("calculator.chart.benefits.work")}
					</TableHead>
					<TableHead className="text-center text-soft-white">
						{t("calculator.chart.benefits.other")}
					</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody className="text-center">
				<TableRow>
					<TableCell className="text-left">
						{t("calculator.chart.benefits.medical-invalidity")}
					</TableCell>
					<TableCell>
						{getTotalPayout("invalidity", "work", "accident")}
					</TableCell>
					<TableCell>
						{getTotalPayout("invalidity", "other", "accident")}
					</TableCell>
					<TableCell>
						{getTotalPayout("invalidity", "work", "illness")}
					</TableCell>
					<TableCell>
						{getTotalPayout("invalidity", "other", "illness")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="text-left">
						{t("calculator.chart.benefits.occupational-disability")}
					</TableCell>
					<TableCell>
						{getTotalPayout("disability", "work", "accident")}
					</TableCell>
					<TableCell>
						{getTotalPayout("disability", "other", "accident")}
					</TableCell>
					<TableCell>
						{getTotalPayout("disability", "work", "illness")}
					</TableCell>
					<TableCell>
						{getTotalPayout("disability", "other", "illness")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="text-left">
						{t("calculator.chart.benefits.death")}
					</TableCell>
					<TableCell>{getTotalPayout("death", "work", "accident")}</TableCell>
					<TableCell>{getTotalPayout("death", "other", "accident")}</TableCell>
					<TableCell>{getTotalPayout("death", "work", "illness")}</TableCell>
					<TableCell>{getTotalPayout("death", "other", "illness")}</TableCell>
				</TableRow>
			</TableBody>
		</Table>
	);
}
